import React, { useCallback, useEffect, useState } from "react";

export interface ToastMessage {
	id: string;
	message: string;
	type: "success" | "error" | "info" | "warning";
	duration?: number; // in milliseconds, default 4000
}

interface ToastProps {
	message: ToastMessage;
	onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ message, onRemove }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);

	const handleRemove = useCallback(() => {
		setIsRemoving(true);
		setTimeout(() => {
			onRemove(message.id);
		}, 300); // Wait for fade out animation
	}, [onRemove, message.id]);

	useEffect(() => {
		// Trigger fade in animation
		setIsVisible(true);

		// Auto-dismiss timer
		const timer = setTimeout(() => {
			handleRemove();
		}, message.duration || 4000);

		return () => clearTimeout(timer);
	}, [message.duration, handleRemove]);

	const getToastStyles = () => {
		switch (message.type) {
			case "success":
				return "bg-gradient-to-br from-green-50 to-green-100/80 dark:from-green-900/30 dark:to-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200";
			case "error":
				return "bg-gradient-to-br from-red-50 to-red-100/80 dark:from-red-900/30 dark:to-red-900/20 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200";
			case "warning":
				return "bg-gradient-to-br from-yellow-50 to-yellow-100/80 dark:from-yellow-900/30 dark:to-yellow-900/20 border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200";
			case "info":
			default:
				return "bg-gradient-to-br from-blue-50 to-blue-100/80 dark:from-blue-900/30 dark:to-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-200";
		}
	};

	const getIcon = () => {
		switch (message.type) {
			case "success":
				return (
					<svg
						className="w-4 h-4 text-green-600 dark:text-green-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M5 13l4 4L19 7"
						/>
					</svg>
				);
			case "error":
				return (
					<svg
						className="w-4 h-4 text-red-600 dark:text-red-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				);
			case "warning":
				return (
					<svg
						className="w-4 h-4 text-yellow-600 dark:text-yellow-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
						/>
					</svg>
				);
			case "info":
			default:
				return (
					<svg
						className="w-4 h-4 text-blue-600 dark:text-blue-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				);
		}
	};

	return (
		<div
			className={`
        flex items-center gap-3 p-4 border shadow-soft-xl rounded-2xl max-w-sm transition-all duration-300 ease-out backdrop-blur-sm
        ${getToastStyles()}
        ${
					isVisible && !isRemoving
						? "transform translate-x-0 opacity-100 scale-100"
						: "transform translate-x-full opacity-0 scale-95"
				}
      `}
		>
			<div className="flex-shrink-0">{getIcon()}</div>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-semibold break-words">{message.message}</p>
			</div>
			<button
				onClick={handleRemove}
				className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 hover:scale-110 active:scale-95 rounded-lg p-1 hover:bg-black/5 dark:hover:bg-white/5"
				aria-label="Close notification"
			>
				<svg
					className="w-3.5 h-3.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2.5"
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			</button>
		</div>
	);
};

interface ToastContainerProps {
	toasts: ToastMessage[];
	onRemoveToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
	toasts,
	onRemoveToast,
}) => {
	return (
		<div className="fixed bottom-4 right-4 z-50 space-y-2 pointer-events-none">
			{toasts.map((toast) => (
				<div key={toast.id} className="pointer-events-auto">
					<Toast message={toast} onRemove={onRemoveToast} />
				</div>
			))}
		</div>
	);
};

export default Toast;
